import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { authApi, tunnelApi } from 'api/main';
import { userSelectors, tunnelActions, tunnelTypes } from 'reducers';
import {
    coSubscriberUpdated,
    subscriptionFormStatusSet,
    tunnelSubscriptionUpdated,
    userTunnelStepsUpdated,
} from 'reducers/user';
import { tunnelSchemas } from 'schemas';
import { investorErrors, UserEnrichments, docTypes } from 'utils/constants';
import { texts } from 'utils/languages/fr';
import { appApi } from 'api';

const {
    INVESTOR_PROFILE,
    USER_TUNNEL_STEPS,
    INVESTMENT_CHARACTERISTICS,
    COMPANY,
    DOCUMENTS,
    KYC_SURVEY,
    SIGNATURES,
    SUBSCRIPTIONS,
    DISTRIBUTOR_COMPANY,
    ALERTS,
    USER_KNOWLEDGE,
    SHARE_SUBCATEGORY,
} = UserEnrichments;

const sendCoSubscriber = function* (action) {
    yield put(tunnelActions.requestSendCoSubscriber());

    try {
        const payload = tunnelSchemas.sendCoSubscriber(action.payload);
        yield call(tunnelApi.sendCoSubscriber, payload);
        const user = yield call(authApi.getMeEnriched, {
            enrich_with: [
                INVESTOR_PROFILE,
                USER_TUNNEL_STEPS,
                KYC_SURVEY,
                SUBSCRIPTIONS,
                ALERTS,
                DOCUMENTS,
                COMPANY,
                USER_KNOWLEDGE,
                INVESTMENT_CHARACTERISTICS,
            ],
        });
        // we need to clear the BS document list
        const tunnelSubscription = yield select(userSelectors.getTunnelSubscription);
        const documentsQueryObject = {
            type: docTypes.SUBSCRIPTION_FORM,
            subscriptionId: tunnelSubscription?.id,
            pagination: false,
        };
        yield put(appApi.util.updateQueryData('getDocuments', documentsQueryObject, () => []));

        yield put(coSubscriberUpdated(user));
        yield put(subscriptionFormStatusSet(false));
        yield put(tunnelActions.receiveSendCoSubscriber());
        action.callback?.();
    } catch (error) {
        if (error.status && ![401, 403, 500].includes(error.status)) {
            if (
                error.data?.errors?.find(
                    err => err.message === investorErrors.CONTACT_INFO_US_PERSON
                )
            ) {
                yield put(
                    tunnelActions.setIneligible({
                        open: true,
                        message: texts.LA_SOUSCRIPTION_N_EST_PAS_POSSIBLE,
                    })
                );
            }
        }
        yield put(tunnelActions.setTunnelErrors(error.data?.errors));
        yield put(tunnelActions.receiveSendCoSubscriberFail(error));
        action.callback?.(error?.data);
    }
};

const sendInvestmentCharacteristics = function* (action) {
    yield put(tunnelActions.requestSendInvestmentCharacteristics());
    const payload = tunnelSchemas.sendInvestmentCharacteristics(action.investmentCharacteristics);

    try {
        yield call(tunnelApi.sendInvestmentCharacteristics, payload);
        const user = yield call(authApi.getMeEnriched, {
            enrich_with: [
                INVESTOR_PROFILE,
                USER_TUNNEL_STEPS,
                INVESTMENT_CHARACTERISTICS,
                SHARE_SUBCATEGORY,
                SUBSCRIPTIONS,
                KYC_SURVEY,
                ALERTS,
                DOCUMENTS,
                COMPANY,
                USER_KNOWLEDGE,
            ],
        });

        // we need to clear the BS document list
        const tunnelSubscription = yield select(userSelectors.getTunnelSubscription);
        const documentsQueryObject = {
            type: docTypes.SUBSCRIPTION_FORM,
            subscriptionId: tunnelSubscription?.id,
            pagination: false,
        };
        yield put(appApi.util.updateQueryData('getDocuments', documentsQueryObject, () => []));

        yield put(tunnelSubscriptionUpdated(user));
        yield put(subscriptionFormStatusSet(false));

        yield put(tunnelActions.receiveSendInvestmentCharacteristics());
        action.callback?.();
    } catch (error) {
        if (error.status && ![400, 401, 403, 500].includes(error.status)) {
            // handling business errors from server:
            // * the bad data is still saved in the DB so we need to update the store
            const user = yield call(authApi.getMeEnriched, {
                enrich_with: [
                    INVESTOR_PROFILE,
                    INVESTMENT_CHARACTERISTICS,
                    USER_TUNNEL_STEPS,
                    SUBSCRIPTIONS,
                    KYC_SURVEY,
                    ALERTS,
                    DOCUMENTS,
                    COMPANY,
                    USER_KNOWLEDGE,
                ],
            });

            // we need to clear the BS document list
            const tunnelSubscription = yield select(userSelectors.getTunnelSubscription);
            const documentsQueryObject = {
                type: docTypes.SUBSCRIPTION_FORM,
                subscriptionId: tunnelSubscription?.id,
                pagination: false,
            };
            yield put(appApi.util.updateQueryData('getDocuments', documentsQueryObject, () => []));

            yield put(tunnelSubscriptionUpdated(user));
            yield put(subscriptionFormStatusSet(false));
        }
        yield put(tunnelActions.receiveSendInvestmentCharacteristicsFail(error));
        action.callback?.(error);
    }
};

const sendAttestationSurHonneur = function* (action) {
    yield put(tunnelActions.requestSendAttestationSurHonneur());

    try {
        yield call(tunnelApi.sendAttestationSurHonneur, action.payload);
        const user = yield call(authApi.getMeEnriched, {
            enrich_with: [
                INVESTOR_PROFILE,
                USER_TUNNEL_STEPS,
                SIGNATURES,
                SUBSCRIPTIONS,
                DOCUMENTS,
                ALERTS,
                COMPANY,
                KYC_SURVEY,
                USER_KNOWLEDGE,
            ],
        });

        // we need to clear the BS document list
        const tunnelSubscription = yield select(userSelectors.getTunnelSubscription);
        const documentsQueryObject = {
            type: docTypes.SUBSCRIPTION_FORM,
            subscriptionId: tunnelSubscription?.id,
            pagination: false,
        };
        yield put(appApi.util.updateQueryData('getDocuments', documentsQueryObject, () => []));

        yield put(tunnelSubscriptionUpdated(user));
        yield put(subscriptionFormStatusSet(false));
        yield put(tunnelActions.receiveSendAttestationSurHonneur());
        action.callback?.();
    } catch (error) {
        yield put(tunnelActions.receiveSendAttestationSurHonneurFail(error));
        action.callback?.(error);
    }
};

const sendDistributorKYC = function* (action) {
    yield put(tunnelActions.requestDistributorKYC());

    try {
        const payload = tunnelSchemas.sendDistributorKYC(action.payload);
        yield call(tunnelApi.sendDistributorKYC, payload);
        const user = yield call(authApi.getMeEnriched, {
            enrich_with: [
                USER_TUNNEL_STEPS,
                SUBSCRIPTIONS,
                ALERTS,
                DOCUMENTS,
                KYC_SURVEY,
                DISTRIBUTOR_COMPANY,
            ],
        });

        // we need to clear the BS document list
        const tunnelSubscription = yield select(userSelectors.getTunnelSubscription);
        const documentsQueryObject = {
            type: docTypes.SUBSCRIPTION_FORM,
            subscriptionId: tunnelSubscription?.id,
            pagination: false,
        };
        yield put(appApi.util.updateQueryData('getDocuments', documentsQueryObject, () => []));

        yield put(tunnelSubscriptionUpdated(user));
        yield put(tunnelActions.receiveDistributorKYC());
        action.callback?.();
    } catch (error) {
        yield put(tunnelActions.receiveDistributorKYCFail(error));
    }
};

const sendPerformancePedagogicalConfirm = function* (action) {
    yield put(tunnelActions.requestPerformancePedagogicalConfirm());

    try {
        yield call(tunnelApi.confirmPerformancePedagogicalStep);
        const user = yield call(authApi.getMeEnriched, {
            enrich_with: [
                USER_TUNNEL_STEPS,
                SUBSCRIPTIONS,
                ALERTS,
                DOCUMENTS,
                COMPANY,
                KYC_SURVEY,
                USER_KNOWLEDGE,
            ],
        });

        // we need to clear the BS document list
        const tunnelSubscription = yield select(userSelectors.getTunnelSubscription);
        const documentsQueryObject = {
            type: docTypes.SUBSCRIPTION_FORM,
            subscriptionId: tunnelSubscription?.id,
            pagination: false,
        };
        yield put(appApi.util.updateQueryData('getDocuments', documentsQueryObject, () => []));

        yield put(userTunnelStepsUpdated(user));
        yield put(tunnelActions.receivePerformancePedagogicalConfirm());
        action.callback?.();
    } catch (error) {
        yield put(tunnelActions.receivePerformancePedagogicalConfirmFail(error));
    }
};

const uploadIdDocument = function* (action) {
    yield put(tunnelActions.requestUploadIdDocument());

    try {
        yield call(tunnelApi.uploadIdDocument, action.payload, action.stepId);
        const user = yield call(authApi.getMeEnriched, {
            enrich_with: [USER_TUNNEL_STEPS, INVESTOR_PROFILE, ALERTS, DOCUMENTS, SUBSCRIPTIONS],
        });

        // we need to clear the BS document list
        const tunnelSubscription = yield select(userSelectors.getTunnelSubscription);
        const documentsQueryObject = {
            type: docTypes.SUBSCRIPTION_FORM,
            subscriptionId: tunnelSubscription?.id,
            pagination: false,
        };
        yield put(appApi.util.updateQueryData('getDocuments', documentsQueryObject, () => []));

        yield put(tunnelSubscriptionUpdated(user));
        yield put(tunnelActions.receiveUploadIdDocument());
        action.callback?.();
    } catch (error) {
        yield put(tunnelActions.receiveUploadIdDocumentFail(error));
    }
};

export default function* () {
    yield all([
        takeLatest(tunnelTypes.SEND_CO_SUBSCRIBER, sendCoSubscriber),
        takeLatest(tunnelTypes.SEND_INVESTMENT_CHARACTERISTICS, sendInvestmentCharacteristics),
        takeLatest(tunnelTypes.SEND_ATTESTATION_SUR_HONNEUR, sendAttestationSurHonneur),
        takeLatest(
            tunnelTypes.SEND_PERFORMANCE_PEDAGOGICAL_CONFIRM,
            sendPerformancePedagogicalConfirm
        ),
        takeLatest(tunnelTypes.SEND_DISTRIBUTOR_KYC, sendDistributorKYC),
        takeLatest(tunnelTypes.UPLOAD_ID_DOCUMENT, uploadIdDocument),
    ]);
}
